import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { downloadFile, readBlobError } from '../utils/fileDownload';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import RouteCard from './RouteCard';
import EventTypesGuideModal from './EventTypesGuideModal';
import EventTeamEntry from './EventTeamEntry';
import EventParticipantsModal from './EventParticipantsModal';
import EventResultsModal from './EventResultsModal';
import CountdownTimer from './CountdownTimer';
import { startsAfterToday } from '../utils/eventStatus';
import './EventsList.css';

// Компонент для отображения описания с tooltip
const DescriptionCell = ({ description }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const textRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      // Проверяем, обрезан ли текст
      setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [description]);

  const handleMouseEnter = () => {
    if (!isOverflowing || !wrapperRef.current) return;
    
    const rect = wrapperRef.current.getBoundingClientRect();
    
    // Создаем временный элемент для измерения размеров tooltip
    const tempTooltip = document.createElement('div');
    tempTooltip.className = 'description-tooltip';
    tempTooltip.style.visibility = 'hidden';
    tempTooltip.style.position = 'fixed';
    tempTooltip.style.top = '-9999px';
    tempTooltip.textContent = description;
    document.body.appendChild(tempTooltip);
    
    const tooltipWidth = tempTooltip.offsetWidth;
    const tooltipHeight = tempTooltip.offsetHeight;
    document.body.removeChild(tempTooltip);
    
    // Позиционируем tooltip над ячейкой
    setTooltipPosition({
      top: rect.top - tooltipHeight - 10,
      left: Math.max(10, rect.left + rect.width / 2 - tooltipWidth / 2)
    });
    
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  if (!description) return <div className="event-description-cell">-</div>;

  return (
    <>
      <div 
        ref={wrapperRef}
        className="event-description-cell-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div ref={textRef} className="event-description-cell">
          {description}
        </div>
      </div>
      {showTooltip && (
        <div 
          className="description-tooltip"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`
          }}
        >
          {description}
        </div>
      )}
    </>
  );
};

// Вкладки списка событий: состояние события определяет getEventStatus,
// события без маршрутов еще не назначены — их место среди запланированных
const EVENT_TABS = ['planned', 'current', 'archive'];
const DEFAULT_TAB = 'current';
const TAB_BY_STATUS = {
  planned: 'planned',
  no_routes: 'planned',
  active: 'current',
  completed: 'archive'
};

const EventsList = () => {
  const { t, language, pluralize } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [schedulePdfEventId, setSchedulePdfEventId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Памятка по типам событий: тексты те же, что в форме создания события
  const [showTypesGuide, setShowTypesGuide] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerTarget = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [activeStatusTooltip, setActiveStatusTooltip] = useState(null);
  const [statusTooltipPosition, setStatusTooltipPosition] = useState({ top: 0, left: 0 });
  const [showTeamRegistrationModal, setShowTeamRegistrationModal] = useState(false);
  const [selectedEventForRegistration, setSelectedEventForRegistration] = useState(null);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [selectedEventForParticipants, setSelectedEventForParticipants] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedEventForResults, setSelectedEventForResults] = useState(null);
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    event_type: ''
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [viewMode, setViewMode] = useState(() => sessionStorage.getItem('eventsViewMode') || 'list');
  // Скрытые события по умолчанию не показываем даже тем, кому они доступны (админ, автор, соорганизатор)
  const [showHidden, setShowHidden] = useState(false);
  // Вкладка живет в адресе: возврат со страницы события открывает ту же вкладку
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = EVENT_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : DEFAULT_TAB;

  const handleTabChange = (tab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    sessionStorage.setItem('eventsViewMode', mode);
  };
  const headerRefs = useRef({});

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.filter-header') && !event.target.closest('.filter-dropdown-fixed')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // silent — обновление без лоадера, чтобы список не мигал (например, после регистрации команды)
  const fetchEvents = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get('/api/events');
      setEvents(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch events:', err);
      if (!silent) {
        setError(err.response?.data?.error || 'Failed to load events');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Точечное обновление одного события в списке — без перезагрузки всей страницы
  const updateEventInList = (eventId, updater) => {
    const apply = (list) => list.map(item => (item.id === eventId ? updater(item) : item));
    setEvents(prev => apply(prev));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setOpenDropdown(null);
  };

  const toggleDropdown = (dropdownName) => {
    if (openDropdown === dropdownName) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(dropdownName);
      const headerElement = headerRefs.current[dropdownName];
      if (headerElement) {
        const rect = headerElement.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX
        });
      }
    }
  };

  const clearFilters = () => {
    setFilters({
      event_type: ''
    });
  };

  const hasActiveFilters = Boolean(filters.event_type);

  const handleDelete = async (eventId) => {
    if (!window.confirm(t('events.confirm_delete'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/events/${eventId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      fetchEvents();
    } catch (err) {
      console.error('Failed to delete event:', err);
      alert(t('events.errors.delete_failed'));
    }
  };

  const handleShowParticipants = (e, event) => {
    e.stopPropagation();
    setSelectedEventForParticipants(event);
    setShowParticipantsModal(true);
  };

  // Расписание печатает бэкенд, а часовой пояс берем из браузера: времена
  // стартов в документе должны совпасть с тем, что человек видит на сайте
  const handleSchedulePdf = async (e, event) => {
    e.stopPropagation();
    setSchedulePdfEventId(event.id);
    try {
      await downloadFile(`/api/events/${event.id}/schedule_pdf`, {
        params: { language, time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        fallbackName: `schedule-${event.id}.pdf`
      });
    } catch (err) {
      console.error('Failed to download schedule PDF', err);
      const details = await readBlobError(err);
      alert([t('events.schedule_pdf_error'), details].filter(Boolean).join(': '));
    } finally {
      setSchedulePdfEventId(null);
    }
  };

  const handleShowResults = (e, event) => {
    e.stopPropagation();
    setSelectedEventForResults(event);
    setShowResultsModal(true);
  };

  const handleMouseEnter = (e, tooltipId) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    
    const tempTooltip = document.createElement('div');
    tempTooltip.className = 'tooltip-global';
    tempTooltip.style.visibility = 'hidden';
    tempTooltip.style.position = 'fixed';
    tempTooltip.style.top = '-9999px';
    
    let tooltipText = '';
    if (tooltipId.startsWith('edit-')) tooltipText = t('common.edit');
    if (tooltipId.startsWith('delete-')) tooltipText = t('common.delete');
    if (tooltipId.startsWith('register-personal-')) tooltipText = t('events.register_personal');
    if (tooltipId.startsWith('register-team-')) tooltipText = t('events.register_team');
    if (tooltipId.startsWith('unregister-')) tooltipText = t('events.unregister');
    if (tooltipId.startsWith('participants-')) tooltipText = t('events.view_participants');
    if (tooltipId.startsWith('results-')) tooltipText = t('events.view_results');
    if (tooltipId.startsWith('schedule-')) tooltipText = t('events.schedule_pdf');
    
    tempTooltip.textContent = tooltipText;
    document.body.appendChild(tempTooltip);
    const tooltipWidth = tempTooltip.offsetWidth;
    document.body.removeChild(tempTooltip);
    
    setTooltipPosition({
      top: rect.top - 40,
      left: rect.left + rect.width / 2 - tooltipWidth / 2
    });
    setActiveTooltip(tooltipId);
  };

  const handleMouseLeave = () => {
    setActiveTooltip(null);
  };

  const handlePersonalRegistration = async (event) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/events/${event.id}/registrations`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const registrationType = response.data?.registration?.registration_type || 'personal';
      updateEventInList(event.id, (item) => ({
        ...item,
        user_registration: { registered: true, registration_type: registrationType },
        registered_users: [
          ...(item.registered_users || []).filter(u => u.user_id !== user.id),
          {
            user_id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            nickname: user.nickname,
            registration_type: registrationType
          }
        ]
      }));
    } catch (err) {
      console.error('Failed to register:', err);
      alert(err.response?.data?.error || t('events.errors.registration_failed'));
    }
  };

  const handleTeamRegistration = (event) => {
    setSelectedEventForRegistration(event);
    setShowTeamRegistrationModal(true);
  };

  const handleUnregister = async (event) => {
    if (!window.confirm(t('events.confirm_unregister'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `/api/events/${event.id}/registrations/current`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      updateEventInList(event.id, (item) => ({
        ...item,
        user_registration: { registered: false },
        registered_users: (item.registered_users || []).filter(u => u.user_id !== user.id)
      }));
    } catch (err) {
      console.error('Failed to unregister:', err);
      alert(t('events.errors.unregister_failed'));
    }
  };

  const isEventActiveOrUpcoming = (event) => {
    const status = getEventStatus(event).status;
    return status === 'active' || status === 'planned';
  };

  const isRegistrationOpen = (event) => {
    if (event.long_event) {
      return true;
    }

    if (getEventStatus(event).status === 'completed') {
      return false;
    }

    // Если registration_closes_hours_before не установлено (null), регистрация открыта всегда
    if (event.registration_closes_hours_before === null || event.registration_closes_hours_before === undefined) {
      return true;
    }

    // Находим самый ранний проезд
    const now = new Date();
    const earliestRoute = event.event_routes.filter(er => er.start_date_time_utc).reduce((earliest, route) => {
      const routeDate = new Date(route.start_date_time_utc);
      return !earliest || routeDate < new Date(earliest.start_date_time_utc) ? route : earliest;
    }, null);

    // В открытом турнире регистрация живет по периоду проведения, а он уже
    // проверен через статус события выше
    if (!earliestRoute) return event.event_type === 'open_tournament';

    const firstRideTime = new Date(earliestRoute.start_date_time_utc);
    const closeTime = new Date(firstRideTime.getTime() - event.registration_closes_hours_before * 60 * 60 * 1000);

    // Регистрация открыта, если текущее время меньше времени закрытия
    return isEventActiveOrUpcoming(event) && now < closeTime;
  };

  // Заявить на событие свою основную команду (с выбором состава) может ее
  // менеджер, если в событии есть командный зачет. В RMC команд нет
  const canRegisterTeam = (event) =>
    Boolean(user && event.team_competition && event.id !== 37 && user.administered_teams?.length > 0);

  const getEventTypeLabel = (type) => {
    return t(`events.types.${type}`);
  };

  const getStatusLabel = (status) => {
    return t(`events.statuses.${status}`);
  };

  const getEarliestRouteDate = (event) => {
    if (!event.event_routes || event.event_routes.length === 0) {
      return null;
    }

    const earliestRoute = event.event_routes.reduce((earliest, route) => {
      const routeDate = new Date(route.start_date_time_utc);
      return !earliest || routeDate < new Date(earliest.start_date_time_utc) ? route : earliest;
    }, null);

    return earliestRoute ? earliestRoute.start_date_time_utc : null;
  };

  const getDaysUntilEvent = (event) => {
    const earliestDate = getEarliestRouteDate(event);
    if (!earliestDate) return null;

    const now = new Date();
    const startDate = new Date(earliestDate);
    const diffTime = startDate - now;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : null;
  };

  const getEventStatus = (event) => {
    // У открытого турнира расписания нет — его состояние задает период проведения
    if (event.event_type === 'open_tournament') {
      const startsAt = event.starts_at ? new Date(event.starts_at) : null;
      const endsAt = event.ends_at ? new Date(event.ends_at) : null;

      if (endsAt && endsAt < new Date()) {
        return { status: 'completed', label: t('events.event_status.completed') };
      }
      if (startsAt && startsAt > new Date()) {
        return { status: 'planned', label: t('events.event_status.planned') };
      }
      return { status: 'active', label: t('events.event_status.active') };
    }

    if (!event.event_routes || event.event_routes.length === 0) {
      return { status: 'no_routes', label: '-' };
    }

    if (event.long_event) {
      return { status: 'active', label: t('events.event_status.active') };
    }

    // Получаем текущее время в UTC для корректного сравнения
    const nowUtc = new Date();
    const nowUtcTime = new Date(nowUtc.getTime());
    // Маршруты открытого турнира старта не имеют: пока такой
    // маршрут в составе события, событие идет
    const eventRoutes = event.event_routes.filter(er => er.start_date_time_utc);

    if (eventRoutes.length === 0) {
      return { status: 'active', label: t('events.event_status.active') };
    }
    
    const pastRoutes = eventRoutes.filter(er => {
      // start_date_time_utc уже в UTC, создаём Date объект напрямую
      const startDateUtc = new Date(er.start_date_time_utc);
      return startDateUtc < nowUtcTime - 60 * 45 * 1000;
    });
    const futureRoutes = eventRoutes.filter(er => startsAfterToday(er.start_date_time_utc));

    if (pastRoutes.length === eventRoutes.length) {
      return { status: 'completed', label: t('events.event_status.completed') };
    }

    if (futureRoutes.length === eventRoutes.length) {
      return { status: 'planned', label: t('events.event_status.planned') };
    }

    return { status: 'active', label: t('events.event_status.active') };
  };

  // Поиск, тип и скрытые события отбираются по всем событиям сразу:
  // счетчики на вкладках показывают, где лежат совпадения
  const filteredEvents = events.filter(event => {
    if (searchQuery && !event.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filters.event_type && event.event_type !== filters.event_type) return false;
    if (filters.registration === 'open' && !isRegistrationOpen(event)) return false;
    if (!showHidden && event.hidden) return false;
    return true;
  });
  const hasHiddenEvents = events.some(event => event.hidden);
  const tabCounts = Object.fromEntries(EVENT_TABS.map(tab => [tab, 0]));
  const tabEvents = [];
  filteredEvents.forEach(event => {
    const tab = TAB_BY_STATUS[getEventStatus(event).status];
    tabCounts[tab] += 1;
    if (tab === activeTab) tabEvents.push(event);
  });

  if (loading) {
    return (
      <div className="events-list-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="events-list-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  const eventTypes = ['group_ride', 'race', 'tournament', 'open_tournament'];

  return (
    <div className={`events-list-container ${isMobile ? 'mobile-view' : ''}`}>
      <div className="events-header">
        <div className="events-header-title">
          <h2>{t('events.title')}</h2>
          <button
            type="button"
            className="types-guide-button"
            onClick={() => setShowTypesGuide(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            {t('events.type_guide.button')}
          </button>
        </div>
        {user && user.can_create_events && (
          <button 
            className="btn-create-event" 
            onClick={() => navigate('/events/new')}
          >
            {t('events.create_new')}
          </button>
        )}
      </div>

      <div className="events-tabs" role="tablist">
        {EVENT_TABS.map(tab => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            className={`events-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => handleTabChange(tab)}
          >
            <span className="events-tab-label">{t(`events.tabs.${tab}`)}</span>
            <span className="events-tab-count">{tabCounts[tab]}</span>
          </button>
        ))}
      </div>

      <div className="events-search">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('events.searchPlaceholder')}
            className="search-input"
          />
          <button type="submit" className="search-button">
            {t('events.search')}
          </button>
          {searchQuery && (
            <button type="button" onClick={handleClearSearch} className="clear-search-button">
              {t('events.clearSearch')}
            </button>
          )}
        </form>
        {searchQuery && (
          <div className="search-info">
            {t('events.searchResults')}: "{searchQuery}"
          </div>
        )}
      </div>

      <div className="events-stats-bar">
        <div className="events-stats">
          {t('events.total_events')}: {tabEvents.length}
        </div>
        <div className="events-stats-bar-right">
          {/* В карточках шапки таблицы нет — фильтр по типу выносим сюда */}
          {(isMobile || viewMode === 'grid') && (
            <select
              className={`events-type-select ${filters.event_type ? 'active' : ''}`}
              value={filters.event_type}
              onChange={(e) => handleFilterChange('event_type', e.target.value)}
              aria-label={t('events.fields.type')}
            >
              <option value="">{t('events.allTypes')}</option>
              {eventTypes.map(type => (
                <option key={type} value={type}>{getEventTypeLabel(type)}</option>
              ))}
            </select>
          )}
          {!isMobile && (
            <div className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => handleViewModeChange('list')}
                aria-label={t('a11y.listView')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6"/>
                  <line x1="8" y1="12" x2="21" y2="12"/>
                  <line x1="8" y1="18" x2="21" y2="18"/>
                  <line x1="3" y1="6" x2="3.01" y2="6"/>
                  <line x1="3" y1="12" x2="3.01" y2="12"/>
                  <line x1="3" y1="18" x2="3.01" y2="18"/>
                </svg>
              </button>
              <button
                className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => handleViewModeChange('grid')}
                aria-label={t('a11y.gridView')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                </svg>
              </button>
            </div>
          )}
          {(hasHiddenEvents || showHidden) && (
            <label className="hidden-events-toggle">
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
              />
              {t('events.show_hidden')}
            </label>
          )}
          {hasActiveFilters && (
            <button onClick={clearFilters} className="clear-filters-btn">
              {t('events.clearFilters')}
            </button>
          )}
        </div>
      </div>

      {tabEvents.length === 0 ? (
        <div className="no-events">
          {searchQuery || hasActiveFilters ? t('events.no_events') : t(`events.tabs.empty_${activeTab}`)}
        </div>
      ) : (
        <>
          {isMobile || viewMode === 'grid' ? (
            <div className={`events-cards-container${!isMobile ? ' desktop-view' : ''}`}>
              <div className="events-cards-grid">
                {tabEvents.map(event => (
                  <div key={event.id} className="event-card" onClick={() => navigate(`/events/${event.id}`)}>
                    {activeTab !== 'archive' && event.event_routes.length > 0 && !isRegistrationOpen(event) && (
                      <span className="registration-closed-badge closed-mobile">
                        {t('events.registration_closed')}
                      </span>
                    )}
                    <div className="event-card-header">
                      {event.image ? (
                        <img 
                          src={event.image} 
                          alt={event.name} 
                          className="event-card-image"
                        />
                      ) : (
                        <div className="event-card-image-placeholder">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    <div className="event-card-body">
                      <div className="event-card-title">{event.name}</div>
                      
                      <div className="event-card-badges">
                        {event.hidden && (
                          <span className="event-hidden-badge">{t('events.hidden_badge')}</span>
                        )}
                        {event.status === 'official' ? (
                          <div className="event-author-badge event-author-official">
                            <span>{t('events.by_user')}</span>
                            <img src="/rouvy.svg" alt="ROUVY" className="rouvy-icon" />
                          </div>
                        ) : event.user ? (
                          <div className="event-author-badge event-author-custom">
                            <span>{t('events.by_user')} {event.user.nickname || `${event.user.first_name} ${event.user.last_name}`}</span>
                          </div>
                        ) : null}
                        <span className={`event-status-badge-computed event-type-${event.event_type}`}>
                          {getEventTypeLabel(event.event_type)}
                        </span>
                        <div>
                          {getEventStatus(event).status !== 'no_routes' && (
                            <span className={`event-status-badge-computed event-status-${getEventStatus(event).status}`}>
                              {getEventStatus(event).label}
                            </span>
                          )}
                          {getEventStatus(event).status !== 'completed' && event.event_routes && event.event_routes.length > 0 && getEarliestRouteDate(event) && (
                            <CountdownTimer targetDate={getEarliestRouteDate(event)} />
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="event-card-footer" onClick={(e) => e.stopPropagation()}>
                      <div className="participants-wrapper">
                        <button
                          onClick={(e) => handleShowParticipants(e, event)}
                          onMouseEnter={(e) => handleMouseEnter(e, `participants-${event.id}`)}
                          onMouseLeave={handleMouseLeave}
                          className="btn-icon-participants"
                          aria-label={t('events.view_participants')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                          </svg>
                        </button>
                        <div>{event.id === 37 ? (event.rmc_participants_count || 0) : (event.registered_users?.length || 0)}</div>
                      </div>
                      {/* Results button - always visible if event has routes */}
                      {event.routes && event.routes.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowResults(e, event);
                          }}
                          className="btn-icon btn-icon-results"
                          aria-label={t('events.view_results')}
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M3 20 L8 20 L8 11 L3 11 Z" />
                            <path d="M10 20 L15 20 L15 6 L10 6 Z" />
                            <path d="M17 20 L22 20 L22 15 L17 15 Z" />
                          </svg>
                        </button>
                      )}

                      {event.routes && event.routes.length > 0 && (
                        <button
                          onClick={(e) => handleSchedulePdf(e, event)}
                          onMouseEnter={(e) => handleMouseEnter(e, `schedule-${event.id}`)}
                          onMouseLeave={handleMouseLeave}
                          className="btn-icon btn-icon-schedule"
                          disabled={schedulePdfEventId === event.id}
                          aria-label={t('events.schedule_pdf')}
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
                            <line x1="3" y1="9.5" x2="21" y2="9.5" />
                            <line x1="8" y1="2.5" x2="8" y2="6.5" />
                            <line x1="16" y1="2.5" x2="16" y2="6.5" />
                            <path d="M12 12.5v5" />
                            <path d="M9.5 15l2.5 2.5 2.5-2.5" />
                          </svg>
                        </button>
                      )}

                      {isRegistrationOpen(event) && event.id !== 37 && (
                        <>
                          {/* Заявка своей команды с выбором состава на событие */}
                          {canRegisterTeam(event) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTeamRegistration(event);
                              }}
                              className="btn-icon btn-icon-register-team"
                              aria-label={t('events.register_team')}
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                              </svg>
                            </button>
                          )}
                          {event.user_registration?.registered ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnregister(event);
                              }}
                              className="btn-icon btn-icon-unregister"
                              aria-label={t('events.unregister')}
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="8.5" cy="7" r="4"></circle>
                                <line x1="18" y1="8" x2="23" y2="13"></line>
                                <line x1="23" y1="8" x2="18" y2="13"></line>
                              </svg>
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePersonalRegistration(event);
                                }}
                                className="btn-icon btn-icon-register"
                                aria-label={t('events.register_personal')}
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                  <circle cx="8.5" cy="7" r="4"></circle>
                                  <line x1="20" y1="8" x2="20" y2="14"></line>
                                  <line x1="23" y1="11" x2="17" y2="11"></line>
                                </svg>
                              </button>
                            </>
                          )}
                        </>
                      )}

                      {(user.admin || user.id === event.user_id) && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/events/${event.id}/edit`);
                            }}
                            className="btn-icon btn-icon-edit"
                            aria-label={t('common.edit')}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(event.id);
                            }}
                            className="btn-icon btn-icon-delete"
                            aria-label={t('common.delete')}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              <line x1="10" y1="11" x2="10" y2="17"/>
                              <line x1="14" y1="11" x2="14" y2="17"/>
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {isLoadingMore && (
                <div className="loading-more">{t('common.loading')}...</div>
              )}
              <div ref={observerTarget} className="observer-target"></div>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="events-table">
            <thead>
              <tr>
                <th>{t('events.fields.name')}</th>
                <th>{t('events.fields.description')}</th>
                <th>
                  <div 
                    className="filter-header" 
                    onClick={() => toggleDropdown('event_type')}
                    ref={el => headerRefs.current['event_type'] = el}
                  >
                    <span>{t('events.fields.type')}</span>
                    <svg className={`filter-icon ${filters.event_type ? 'active' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </th>
                <th>{t('events.fields.status')}</th>
                <th>{t('events.routes')}</th>
                {user && <th>{t('users.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {tabEvents.map(event => (
                <tr key={event.id} onClick={() => navigate(`/events/${event.id}`)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div className="event-name">
                      {event.image ? (
                        <img 
                          src={event.image} 
                          alt={event.name} 
                          className="event-image-small"
                        />
                      ) : (
                        <div className="event-image-placeholder">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                        </div>
                      )}
                      <div>
                        <div className="event-name-text">{event.name}</div>
                        <div className="event-status-wrapper">
                          {event.hidden && (
                            <span className="event-hidden-badge">{t('events.hidden_badge')}</span>
                          )}
                          <div className="participants-wrapper">
                            <button
                              onClick={(e) => handleShowParticipants(e, event)}
                              onMouseEnter={(e) => handleMouseEnter(e, `participants-${event.id}`)}
                              onMouseLeave={handleMouseLeave}
                              className="btn-icon-participants"
                              aria-label={t('events.view_participants')}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                              </svg>
                            </button>
                            <div>{event.id === 37 ? (event.rmc_participants_count || 0) : (event.registered_users?.length || 0)}</div>
                          </div>
                          {event.status === 'official' ? (
                            <div className="event-author-badge event-author-official">
                              <span>{t('events.by_user')}</span>
                              <img src="/rouvy.svg" alt="ROUVY" className="rouvy-icon-list" height="10" />
                            </div>
                          ) : event.user ? (
                            <div className="event-author-badge event-author-custom">
                              <span>{t('events.by_user')} {event.user.nickname || `${event.user.first_name} ${event.user.last_name}`}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <DescriptionCell description={event.description} />
                  </td>
                  <td>
                    <span className={`event-type-badge event-type-${event.event_type}`}>
                      {getEventTypeLabel(event.event_type)}
                    </span>
                  </td>
                  <td>
                    <div className="event-status-cell">
                      {getEventStatus(event).status !== 'no_routes' ? (
                        <>
                          <span 
                            className={`event-status-badge-computed event-status-${getEventStatus(event).status}`}
                            style={{ cursor: 'default' }}
                          >
                            {getEventStatus(event).label}
                          </span>
                          {getEventStatus(event).status !== 'completed' && event.event_routes && event.event_routes.length > 0 && getEarliestRouteDate(event) && (
                            <div>
                              <CountdownTimer targetDate={getEarliestRouteDate(event)} />
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="no-routes-text">-</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="event-routes-count">
                      {event.routes && event.routes.length > 0 ? (
                        <span className="routes-badge routes-badge-text">
                          {language === 'ru' 
                            ? `${event.routes.length} ${pluralize(event.routes.length, t('events.route'), t('events.routes_few'), t('events.routes_many'))}`
                            : `${event.routes.length} ${event.routes.length === 1 ? t('events.route') : t('events.routes_many')}`
                          }
                        </span>
                      ) : (
                        <span className="no-routes-text">-</span>
                      )}
                      {activeTab !== 'archive' && event.routes.length > 0 && !isRegistrationOpen(event) && (
                        <span className="registration-closed-badge closed-desktop">
                          {t('events.registration_closed')}
                        </span>
                      )}
                    </div>
                  </td>
                  {user && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="event-list-actions">
                        {/* Results button - always visible if event has routes */}
                        {event.routes && event.routes.length > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowResults(e, event);
                            }}
                            onMouseEnter={(e) => handleMouseEnter(e, `results-${event.id}`)}
                            onMouseLeave={handleMouseLeave}
                            className="btn-icon btn-icon-results"
                            aria-label={t('events.view_results')}
                          >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M3 20 L8 20 L8 11 L3 11 Z" />
                            <path d="M10 20 L15 20 L15 6 L10 6 Z" />
                            <path d="M17 20 L22 20 L22 15 L17 15 Z" />
                          </svg>
                          </button>
                        )}

                        {event.routes && event.routes.length > 0 && (
                        <button
                            onClick={(e) => handleSchedulePdf(e, event)}
                            onMouseEnter={(e) => handleMouseEnter(e, `schedule-${event.id}`)}
                            onMouseLeave={handleMouseLeave}
                            className="btn-icon btn-icon-schedule"
                            disabled={schedulePdfEventId === event.id}
                            aria-label={t('events.schedule_pdf')}
                          >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
                              <line x1="3" y1="9.5" x2="21" y2="9.5" />
                              <line x1="8" y1="2.5" x2="8" y2="6.5" />
                              <line x1="16" y1="2.5" x2="16" y2="6.5" />
                              <path d="M12 12.5v5" />
                              <path d="M9.5 15l2.5 2.5 2.5-2.5" />
                            </svg>
                          </button>
                        )}

                        {/* Registration actions for active/upcoming events */}
                        {isRegistrationOpen(event) && event.id !== 37 && (
                          <>
                            {/* Заявка своей команды с выбором состава на событие */}
                            {canRegisterTeam(event) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTeamRegistration(event);
                                }}
                                onMouseEnter={(e) => handleMouseEnter(e, `register-team-${event.id}`)}
                                onMouseLeave={handleMouseLeave}
                                className="btn-icon btn-icon-register-team"
                                aria-label={t('events.register_team')}
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                  <circle cx="9" cy="7" r="4"></circle>
                                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                              </button>
                            )}
                            {event.user_registration?.registered ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnregister(event);
                                }}
                                onMouseEnter={(e) => handleMouseEnter(e, `unregister-${event.id}`)}
                                onMouseLeave={handleMouseLeave}
                                className="btn-icon btn-icon-unregister"
                                aria-label={t('events.unregister')}
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                  <circle cx="8.5" cy="7" r="4"></circle>
                                  <line x1="18" y1="8" x2="23" y2="13"></line>
                                  <line x1="23" y1="8" x2="18" y2="13"></line>
                                </svg>
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePersonalRegistration(event);
                                  }}
                                  onMouseEnter={(e) => handleMouseEnter(e, `register-personal-${event.id}`)}
                                  onMouseLeave={handleMouseLeave}
                                  className="btn-icon btn-icon-register"
                                  aria-label={t('events.register_personal')}
                                >
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="8.5" cy="7" r="4"></circle>
                                    <line x1="20" y1="8" x2="20" y2="14"></line>
                                    <line x1="23" y1="11" x2="17" y2="11"></line>
                                  </svg>
                                </button>
                              </>
                            )}
                          </>
                        )}
                        
                        {/* Edit and delete actions for admin/owner */}
                        {(user.admin || user.id === event.user_id) && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/events/${event.id}/edit`);
                              }}
                              onMouseEnter={(e) => handleMouseEnter(e, `edit-${event.id}`)}
                              onMouseLeave={handleMouseLeave}
                              className="btn-icon btn-icon-edit"
                              aria-label={t('common.edit')}
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(event.id);
                              }}
                              onMouseEnter={(e) => handleMouseEnter(e, `delete-${event.id}`)}
                              onMouseLeave={handleMouseLeave}
                              className="btn-icon btn-icon-delete"
                              aria-label={t('common.delete')}
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                <line x1="10" y1="11" x2="10" y2="17"/>
                                <line x1="14" y1="11" x2="14" y2="17"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          )}
        </>
      )}

      {openDropdown === 'event_type' && (
        <div 
          className="filter-dropdown-fixed"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
        >
          <div 
            className={`filter-dropdown-option ${!filters.event_type ? 'selected' : ''}`}
            onClick={() => handleFilterChange('event_type', '')}
          >
            {t('events.allTypes')}
          </div>
          {eventTypes.map(type => (
            <div 
              key={type} 
              className={`filter-dropdown-option ${filters.event_type === type ? 'selected' : ''}`}
              onClick={() => handleFilterChange('event_type', type)}
            >
              {getEventTypeLabel(type)}
            </div>
          ))}
        </div>
      )}

      {activeTooltip && (
        <div 
          className="tooltip tooltip-global"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            opacity: 1
          }}
        >
          {activeTooltip.startsWith('edit-') && t('common.edit')}
          {activeTooltip.startsWith('delete-') && t('common.delete')}
          {activeTooltip.startsWith('register-personal-') && t('events.register_personal')}
          {activeTooltip.startsWith('register-team-') && t('events.register_team')}
          {activeTooltip.startsWith('unregister-') && t('events.unregister')}
          {activeTooltip.startsWith('participants-') && t('events.view_participants')}
          {activeTooltip.startsWith('results-') && t('events.view_results')}
          {activeTooltip.startsWith('schedule-') && t('events.schedule_pdf')}
        </div>
      )}

      {activeStatusTooltip && (
        <div 
          className="tooltip tooltip-global"
          style={{
            top: `${statusTooltipPosition.top}px`,
            left: `${statusTooltipPosition.left}px`,
            opacity: 1
          }}
        >
          {(() => {
            const event = events.find(e => e.id === activeStatusTooltip);
            if (!event) return null;
            const daysUntil = getDaysUntilEvent(event);
            if (!daysUntil) return null;
            return language === 'ru' 
              ? `${t('events.starts_in')} ${daysUntil} ${pluralize(daysUntil, t('events.day'), t('events.days_few'), t('events.days_many'))}`
              : `${t('events.starts_in')} ${daysUntil} ${daysUntil === 1 ? t('events.day') : t('events.days_many')}`;
          })()}
        </div>
      )}

      {showTeamRegistrationModal && selectedEventForRegistration && (
        <EventTeamEntry
          event={selectedEventForRegistration}
          preferExisting
          onClose={() => {
            setShowTeamRegistrationModal(false);
            setSelectedEventForRegistration(null);
          }}
          onDone={() => {
            setShowTeamRegistrationModal(false);
            setSelectedEventForRegistration(null);
            fetchEvents({ silent: true });
          }}
        />
      )}

      {showParticipantsModal && selectedEventForParticipants && (
        <EventParticipantsModal
          event={selectedEventForParticipants}
          onClose={() => {
            setShowParticipantsModal(false);
            setSelectedEventForParticipants(null);
          }}
        />
      )}

      {showResultsModal && selectedEventForResults && (
        <EventResultsModal
          selectedEventId={selectedEventForResults.id}
          onClose={() => {
            setShowResultsModal(false);
            setSelectedEventForResults(null);
          }}
        />
      )}

      <EventTypesGuideModal
        isOpen={showTypesGuide}
        onClose={() => setShowTypesGuide(false)}
      />
    </div>
  );
};

export default EventsList;